// Scan History page — cross-engagement task history with filter tabs,
// stat cards, and a dense table. Auto-refreshes every 5s.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Loading } from "@/components/ui/Loading";
import { PageHeader } from "@/components/ui/PageHeader";
import { Stat } from "@/components/ui/Stat";
import {
  useTaskHistory,
  useTaskStats,
} from "@/hooks/useTaskHistory";
import type { TaskHistoryItem } from "@/types/task";
import { useDict, fmt } from "@/i18n/LocaleProvider";

const STATUS_COLORS: Record<string, string> = {
  queued: "rgb(var(--color-warning))",
  running: "rgb(var(--color-accent))",
  completed: "rgb(var(--color-success))",
  failed: "rgb(var(--color-danger))",
  cancelled: "rgb(var(--color-muted))",
  retrying: "rgb(var(--color-warning))",
  timeout: "rgb(var(--color-danger))",
};

const PRIORITY_COLORS: Record<string, string> = {
  high: "rgb(var(--color-danger))",
  normal: "rgb(var(--color-accent))",
  low: "rgb(var(--color-muted))",
};

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function formatRelativeTime(
  iso: string | null,
  ago: { seconds: string; minutes: string; hours: string },
): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return ago.seconds.replace("{n}", String(Math.floor(diff)));
  if (diff < 3600) return ago.minutes.replace("{n}", String(Math.floor(diff / 60)));
  if (diff < 86400) return ago.hours.replace("{n}", String(Math.floor(diff / 3600)));
  return d.toLocaleDateString();
}

const GRID_COLUMNS = "60px 1.5fr 100px 90px 1fr 120px 90px 100px";

export function ScanHistoryPage() {
  const sh = useDict().app.scanHistory;
  const navigate = useNavigate();
  const [filter, setFilter] = useState<string>("all");
  const tasks = useTaskHistory(filter);
  const stats = useTaskStats();

  if (tasks.isError) {
    const msg =
      tasks.error instanceof Error ? tasks.error.message : sh.unknownError;
    return (
      <Card className="border-danger">
        <div className="text-danger font-mono text-[13px]">{sh.errorPrefix} {msg}</div>
      </Card>
    );
  }

  if (tasks.isLoading || !tasks.data) {
    return <Loading text={sh.loading} />;
  }

  const taskList = tasks.data;
  const s = stats.data;

  return (
    <div>
      <PageHeader
        title={sh.title}
        subtitle={sh.subtitle}
      />

      {/* Stat cards — 2-up on mobile, 4-up from sm+ */}
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Card>
            <Stat label={sh.totalTasks} value={s.total} color="rgb(var(--color-content))" />
          </Card>
          <Card>
            <Stat
              label={sh.activeNow}
              value={s.active}
              color={s.active > 0 ? "rgb(var(--color-accent))" : "rgb(var(--color-dim))"}
              sub={s.active > 0 ? sh.activeSub : sh.idleSub}
            />
          </Card>
          <Card>
            <Stat
              label={sh.completed}
              value={s.by_status.completed ?? 0}
              color="rgb(var(--color-success))"
            />
          </Card>
          <Card>
            <Stat
              label={sh.failed}
              value={(s.by_status.failed ?? 0) + (s.by_status.timeout ?? 0)}
              color="rgb(var(--color-danger))"
            />
          </Card>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 mb-3 flex-wrap">
        {(Object.keys(sh.filters) as Array<keyof typeof sh.filters>).map((key) => {
          const active = filter === key;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className="px-[14px] py-[6px] rounded font-mono text-[11px] font-semibold tracking-[0.02em] cursor-pointer"
              style={{
                background: active ? "rgb(var(--color-accent-bg))" : "transparent",
                border: `1px solid ${active ? "rgb(var(--color-accent))" : "rgb(var(--color-stroke))"}`,
                color: active ? "rgb(var(--color-accent))" : "rgb(var(--color-muted))",
              }}
            >
              {sh.filters[key]}
            </button>
          );
        })}
      </div>

      {/* Task list — fixed grid table from md+, stacked card layout below.
          The table's 8 fixed-width columns (~560px min) horizontally
          scroll on phones, which is unusable. Below md we render the
          same data as compact cards. */}
      {taskList.length === 0 ? (
        <Card>
          <div className="text-dim font-mono text-[12px] text-center py-6">
            {sh.noTasks}
          </div>
        </Card>
      ) : (
        <>
          {/* Mobile (<md): card view */}
          <div className="md:hidden flex flex-col gap-3">
            {taskList.map((t) => (
              <TaskCard
                key={t.id}
                task={t}
                onClick={() => navigate(`/app/engagements/${t.engagement_id}`)}
              />
            ))}
          </div>

          {/* Desktop (md+): grid table */}
          <Card className="!p-0 overflow-hidden hidden md:block">
            <div
              className="grid gap-3 px-4 py-3 bg-bg2 border-b border-stroke font-mono text-[10px] text-muted uppercase tracking-[0.05em] font-semibold"
              style={{ gridTemplateColumns: GRID_COLUMNS }}
            >
              <div>{sh.colNum}</div>
              <div>{sh.colEngagement}</div>
              <div>{sh.colStatus}</div>
              <div>{sh.colPriority}</div>
              <div>{sh.colPhase}</div>
              <div>{sh.colResults}</div>
              <div>{sh.colDuration}</div>
              <div>{sh.colWhen}</div>
            </div>
            {taskList.map((t) => {
              const isActive = t.status === "running" || t.status === "queued";
              const statusColor = STATUS_COLORS[t.status] ?? "rgb(var(--color-muted))";
              const priorityColor = PRIORITY_COLORS[t.priority] ?? "rgb(var(--color-muted))";
              return (
                <div
                  key={t.id}
                  onClick={() => navigate(`/app/engagements/${t.engagement_id}`)}
                  className="grid gap-3 px-4 py-3 border-b border-stroke font-mono text-[11px] cursor-pointer items-center transition-colors duration-150 hover:bg-bg2"
                  style={{ gridTemplateColumns: GRID_COLUMNS }}
                >
                  <div className="text-dim font-semibold">#{t.display_number ?? "—"}</div>
                  <div>
                    <div className="text-content font-semibold text-[12px]">
                      {t.engagement_name}
                    </div>
                    {t.engagement_pentest_type && (
                      <div className="text-muted text-[9px] mt-[2px] uppercase tracking-[0.05em]">
                        {t.engagement_pentest_type.replace("_", " ")}
                      </div>
                    )}
                  </div>
                  <div>
                    <span
                      className="inline-block px-2 py-[3px] rounded-[3px] text-[9px] font-bold tracking-[0.05em] uppercase"
                      style={{
                        background: `${statusColor}22`,
                        border: `1px solid ${statusColor}66`,
                        color: statusColor,
                      }}
                    >
                      {sh.statuses[t.status as keyof typeof sh.statuses] ?? t.status}
                    </span>
                    {t.retry_count > 0 && (
                      <div className="mt-[3px] text-[9px] text-warning">
                        ↻ {fmt(sh.retry, { n: t.retry_count })}
                      </div>
                    )}
                  </div>
                  <div>
                    <span
                      className="inline-block px-2 py-[3px] rounded-[3px] text-[9px] font-bold tracking-[0.05em] uppercase"
                      style={{
                        background: `${priorityColor}22`,
                        border: `1px solid ${priorityColor}66`,
                        color: priorityColor,
                      }}
                    >
                      {sh.priorities[t.priority as keyof typeof sh.priorities] ?? t.priority}
                    </span>
                  </div>
                  <div>
                    <div className="text-content text-[11px] mb-[3px]">
                      {sh.phases[t.current_phase as keyof typeof sh.phases] ??
                        t.current_phase ??
                        "—"}
                    </div>
                    {isActive && (
                      <div className="h-[3px] bg-bg2 rounded-[2px] overflow-hidden">
                        <div
                          className="h-full bg-accent transition-[width] duration-300"
                          style={{ width: `${t.progress_pct ?? 0}%` }}
                        />
                      </div>
                    )}
                    {t.error_message && (
                      <div className="text-danger text-[9px] mt-[3px] overflow-hidden text-ellipsis whitespace-nowrap">
                        {t.error_message}
                      </div>
                    )}
                  </div>
                  <div className="text-content">
                    <div>{t.evidence_count} {sh.evShort}</div>
                    <div
                      className={
                        t.findings_count > 0 ? "text-warning" : "text-muted"
                      }
                    >
                      {t.findings_count} {sh.findShort}
                    </div>
                  </div>
                  <div className="text-muted">
                    {formatDuration(t.duration_seconds)}
                  </div>
                  <div className="text-muted">
                    {formatRelativeTime(t.created_at, sh.timeAgo)}
                  </div>
                </div>
              );
            })}
          </Card>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Mobile-only task card. Renders the same data as one row of the
// desktop grid, but stacked so it fits a phone viewport without
// horizontal scroll. Density is tuned for one-thumb scanning:
// engagement name first, status + when on the second row, then a
// metadata strip (priority / phase / results / duration).
// ──────────────────────────────────────────────────────────────

interface TaskCardProps {
  task: TaskHistoryItem;
  onClick: () => void;
}

function TaskCard({ task: t, onClick }: TaskCardProps) {
  const sh = useDict().app.scanHistory;
  const isActive = t.status === "running" || t.status === "queued";
  const statusColor = STATUS_COLORS[t.status] ?? "rgb(var(--color-muted))";
  const priorityColor = PRIORITY_COLORS[t.priority] ?? "rgb(var(--color-muted))";
  return (
    <Card
      onClick={onClick}
      className="cb-grow"
      style={{ borderColor: isActive ? "rgb(var(--color-accent))" : undefined }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 mb-[2px]">
            <span className="font-mono text-[10px] text-dim font-semibold">
              #{t.display_number ?? "—"}
            </span>
            <span className="font-sans text-[14px] font-semibold text-content truncate">
              {t.engagement_name}
            </span>
          </div>
          {t.engagement_pentest_type && (
            <div className="font-mono text-[9px] text-muted uppercase tracking-[0.05em]">
              {t.engagement_pentest_type.replace("_", " ")}
            </div>
          )}
        </div>
        <span
          className="inline-block px-2 py-[3px] rounded-[3px] text-[9px] font-bold tracking-[0.05em] uppercase shrink-0"
          style={{
            background: `${statusColor}22`,
            border: `1px solid ${statusColor}66`,
            color: statusColor,
          }}
        >
          {sh.statuses[t.status as keyof typeof sh.statuses] ?? t.status}
        </span>
      </div>

      {/* Phase + active progress bar */}
      <div className="font-mono text-[11px] text-content mb-1">
        {sh.phases[t.current_phase as keyof typeof sh.phases] ?? t.current_phase ?? "—"}
      </div>
      {isActive && (
        <div className="h-[3px] bg-bg2 rounded-[2px] overflow-hidden mb-2">
          <div
            className="h-full bg-accent transition-[width] duration-300"
            style={{ width: `${t.progress_pct ?? 0}%` }}
          />
        </div>
      )}
      {t.error_message && (
        <div className="text-danger font-mono text-[9px] mb-2 overflow-hidden text-ellipsis whitespace-nowrap">
          {t.error_message}
        </div>
      )}

      {/* Metadata strip: priority · results · duration · when */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-muted">
        <span style={{ color: priorityColor }}>
          ● {sh.priorities[t.priority as keyof typeof sh.priorities] ?? t.priority}
        </span>
        <span>{t.evidence_count} {sh.evShort}</span>
        <span className={t.findings_count > 0 ? "text-warning" : ""}>
          {t.findings_count} {sh.findShort}
        </span>
        <span>{formatDuration(t.duration_seconds)}</span>
        <span className="ml-auto">{formatRelativeTime(t.created_at, sh.timeAgo)}</span>
      </div>
      {t.retry_count > 0 && (
        <div className="mt-1 font-mono text-[9px] text-warning">
          ↻ {fmt(sh.retry, { n: t.retry_count })}
        </div>
      )}
    </Card>
  );
}
